import type { LiveAdBrandData } from '@moojo/ad-delivery-api'
import type { Duration } from '@moojo/duration'
import type { Logger } from '@moojo/logger'
import { failMe } from '@moojo/misc-constructs'
import type { ConvoAdDetails, RecommendConvoAdRequestQueryParams } from '@moojo/moojo-agent-api'
import { RecommendConvoAdResponse } from '@moojo/moojo-agent-api'
import type { PublisherSlug } from '@moojo/shared-config'
import { knownPublishers, voiceflowConfig } from '@moojo/shared-config'
import type { StatsigClient } from '@statsig/js-client'

import {
  CompositeEventTracker,
  LoggerEventTracker,
  MixpanelEventTracker,
  StatsigEventTracker,
} from '../analytics/event-tracker'
import { applyLauncherTextExperiment, applySiamoNoiLauncherTextExperiment } from '../experiments/launcher-text'
import { DOMUtils } from '../lib/dom-utils'
import { isMobileBrowser } from '../lib/is-mobile-browser'
import { initStatsig } from '../lib/statsig-client'
import { createVoiceflowMessageHandler } from '../lib/voiceflow-event-utils'
import { VoiceflowService } from '../lib/voiceflow-service'

export type ConvoAgentOptions = {
  /**
   * Auto-open behavior configuration.
   *
   * - Duration: Time to wait after page load before automatically opening the widget
   * - 'disabled': Disables auto-open functionality entirely
   */
  autoOpenDelay: Duration | 'disabled'
  /**
   * Forces a publihser display name to be used.
   */
  customPublisherDisplayName?: string
}

const defaultOptions: ConvoAgentOptions = { autoOpenDelay: 'disabled' }

export class ConvoAgentCore {
  private constructor(
    private readonly logger: Logger,
    private readonly vfService: VoiceflowService | undefined,
    private readonly cleanup: () => void,
  ) {}

  static async load(
    logger: Logger,
    publisher: PublisherSlug | undefined,
    aDocument: Document,
    requestedAdId: string | undefined,
    options?: Partial<ConvoAgentOptions>,
  ): Promise<ConvoAgentCore> {
    const domUtils = new DOMUtils(aDocument)
    return this.doLoad(
      logger,
      publisher,
      aDocument,
      domUtils,
      domUtils.lang,
      true,
      { ...options, ...defaultOptions },
      () => this.fetchAd(domUtils.location.href, domUtils.lang, requestedAdId),
    )
  }

  static async loadForPreview(
    logger: Logger,
    aDocument: Document,
    lang: string,
    side: 'left' | 'right',
    ad: ConvoAdDetails,
    options?: Partial<ConvoAgentOptions>,
  ): Promise<ConvoAgentCore> {
    return this.doLoad(
      logger,
      undefined,
      aDocument,
      new DOMUtils(aDocument),
      lang,
      false,
      { ...options, ...defaultOptions },
      async () =>
        ({
          match: true,
          adDetails: ad,
          voiceflowSettings: { projectId: voiceflowConfig.defaultProjectId, side },
        } satisfies RecommendConvoAdResponse),
    )
  }

  changeBrandData(brandData: LiveAdBrandData, side: 'left' | 'right' = 'right') {
    this.logger.info('Changing brand data')
    this.vfService?.changeAppearance(makeAppearanceSettings(brandData, side))
  }

  destroy() {
    this.logger.info('Destroying chat')
    this.cleanup()
    this.vfService?.destroy()
  }

  private static async doLoad(
    logger: Logger,
    publisher: PublisherSlug | undefined,
    aDocument: Document,
    domUtils: DOMUtils,
    lang: string,
    shouldReportEvents: boolean,
    options: ConvoAgentOptions,
    getConvoAdResponse: () => Promise<RecommendConvoAdResponse>,
  ): Promise<ConvoAgentCore> {
    logger.info(`Loading ConvoAd chat widget...`)
    const t0 = Date.now()

    const queryParams = domUtils.getSearchParams('agent', 'moojoDebug', 'vfProjectId')
    const agent = queryParams.agent === 'dev' ? 'dev' : 'prod'
    const debug = Boolean(queryParams.moojoDebug) ?? false

    const isMobile = isMobileBrowser()

    if (debug) {
      logger.info(`>>> Debug mode ENABLED <<<`)
      // Debug mode side effects:
      // - Events will be logged to console instead of being sent to analytics
    }

    if (queryParams.vfProjectId) {
      logger.info(`Overriding vfProjectId: ${queryParams.vfProjectId}`)
    }

    logger.info(`Publisher: ${publisher}`)
    logger.info(`Using language: ${lang}`)
    logger.info(`Using agent: ${agent.toUpperCase()}`)
    logger.info(`Mobile browser: ${isMobile}`)
    logger.info(
      `Auto open delay: ${options.autoOpenDelay === 'disabled' ? 'DISABLED' : `${options.autoOpenDelay.toString()}`}`,
    )

    const statsigClient = await initStatsig()
    const eventTracker =
      shouldReportEvents && !debug
        ? new CompositeEventTracker([
            new StatsigEventTracker(statsigClient),
            new MixpanelEventTracker('08d547303616adbfe114c9fed1fa2d1e'),
          ])
        : new LoggerEventTracker(logger)

    eventTracker.track('load-started', { location: domUtils.location.href, lang })

    // load vf assets, fetch ad, and initialize statsig concurrently
    const [adResponse, vfService] = await Promise.all([
      getConvoAdResponse(),
      VoiceflowService.load(logger, eventTracker, domUtils, agent),
    ])

    if (!adResponse.match) {
      eventTracker.track('load-completed', { withAd: 'false' })

      logger.info(`No matching ads, chat widget loading skipped`)
      return new ConvoAgentCore(logger, undefined, () => {})
    }

    eventTracker.track('loading') // TODO(roman): add more details here (adId, brandName, etc)

    const { adDetails, voiceflowSettings } = adResponse

    const { launcherText, vfProjectId } = this.applyExperiments(
      statsigClient,
      publisher,
      lang,
      adDetails.brandName,
      adDetails.launcherText,
      queryParams.vfProjectId ?? voiceflowSettings.projectId,
    )

    const publisherDisplayName =
      options.customPublisherDisplayName ?? (publisher ? findPublisher(publisher).displayName : 'Unknown')

    eventTracker.updateContext({
      vfProjectId,
      publisher: publisherDisplayName,
      brandName: adDetails.brandName,
    })

    // Set up voiceflow event handling
    const messageHandler = createVoiceflowMessageHandler(eventTracker)
    window.addEventListener('message', messageHandler)

    await vfService.show(
      {
        projectId: vfProjectId,
        persistence: 'memory',
        launcherText,
        placeholderText: adDetails.placeholderText,
        headerText: adDetails.widgetHeaderText ?? adDetails.launcherText,
        appearance: makeAppearanceSettings(adDetails.brand, voiceflowSettings.side),
      },
      { ...adDetails, publisher: publisherDisplayName, lang, title: domUtils.title },
    )

    const cancelAutoOpen =
      options.autoOpenDelay !== 'disabled' && !isMobile
        ? this.scheduleAutoOpen(aDocument, vfService, options.autoOpenDelay.whole('millis'))
        : undefined

    logger.info(
      `ConvoAd chat widget loaded. took: ${Date.now() - t0} msc | vfProjectId: ${vfProjectId} | adBrandName: ${
        adDetails.brandName
      } | adServingUrl: ${adDetails.servingUrl}`,
    )

    eventTracker.track('load-completed', { withAd: 'true' })

    return new ConvoAgentCore(logger, vfService, () => {
      window.removeEventListener('message', messageHandler)
      cancelAutoOpen?.()
    })
  }

  private static scheduleAutoOpen(aDocument: Document, vfService: VoiceflowService, delayInMillis: number): () => void {
    if (aDocument.readyState === 'complete') {
      const timeoutId = setTimeout(() => vfService.open(), delayInMillis)
      return () => clearTimeout(timeoutId)
    }
    // else - wait for load event

    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const loadHandler = () => {
      timeoutId = setTimeout(() => vfService.open(), delayInMillis)
    }

    aDocument.addEventListener('load', loadHandler, { once: true })

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      aDocument.removeEventListener('load', loadHandler)
    }
  }

  // Not using ServiceClient as we want force GET request, to spare any CORS slow downs.
  // TODO(roman): support GET request (if empty body) by ServiceClient
  private static async fetchAd(location: string, lang: string, requestedAdId: string | undefined) {
    // the next line is to enforce consistency and type checking against the server side
    // code in the endpoint.
    // TODO(roman): find a better approach.
    const parsedParams: RecommendConvoAdRequestQueryParams = {
      location,
      lang,
      adId: requestedAdId,
    }

    const params = new URLSearchParams()
    Object.entries(parsedParams).forEach(([key, value]) => {
      if (value !== undefined) {
        params.set(key, value)
      }
    })

    const url = `https://cybertron.moojo.id/moojo-agent/ad/convo-recommend?${params.toString()}`
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch ad: ${response.statusText}`)
    }

    const { data } = await response.json()
    return RecommendConvoAdResponse.parse(data)
  }

  private static applyExperiments(
    statsigClient: StatsigClient,
    publisher: PublisherSlug | undefined,
    lang: string,
    brandName: string,
    launcherText: string,
    vfProjectId: string,
  ) {
    if (brandName === 'SIAMO NOI') {
      return {
        launcherText: applySiamoNoiLauncherTextExperiment(statsigClient, lang) || launcherText,
        vfProjectId,
      }
    }

    if (publisher === 'dix30' || publisher === 'royalmount') {
      return {
        launcherText: applyLauncherTextExperiment(statsigClient, brandName) || launcherText,
        vfProjectId,
      }
    }

    return {
      launcherText,
      vfProjectId,
    }
  }
}

const makeAppearanceSettings = (brand: LiveAdBrandData, side: 'left' | 'right') => ({
  headerIconUrl: brand.logoUrl,
  headerIconBackgroundColor: brand.primaryColor,
  headerFontColor: brand.secondaryColor,
  headerBackgroundColor: brand.primaryColor,
  avatarIconUrl: brand.logoUrl,
  avatarIconBackgroundColor: brand.primaryColor,
  launcherIconUrl: brand.logoUrl,
  launcherBackgroundColor: brand.primaryColor,
  launcherFontColor: brand.secondaryColor,
  launcherIconBackgroundColor: brand.primaryColor,
  quickActionBackgroundColor: brand.primaryColor,
  quickActionFontColor: brand.secondaryColor,
  fontColor: brand.fontColor,
  side,
})

function findPublisher(slug: PublisherSlug) {
  return knownPublishers.find(p => p.slug === slug) ?? failMe(`publisher slug not found: "${slug}"`)
}
